import { Button } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { ContainerJourneyStatus } from '@planning/components/molecules/ContainerJourneyStatusChip'
import { INonNumericReleaseItem } from '@planning/rt-stores/nnrOrder/NnrOrderItem'
import { IOrderItem } from '@planning/rt-stores/order/OrderItem'
import { nnrOrderService } from '@planning/services'
import { useTranslate } from '@tolgee/react'
import { ButtonOption } from '@tom-ui/ui'
import { PATHS } from '@tom-ui/utils'
import _ from 'lodash'
import { MRT_Row } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { OptionsButton } from 'modules/ui/src/theme/components/button/OptionsButton'
import { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { ReleaseDetailsViewStore } from '../../Stores/ReleaseDetailsViewStore'

interface Props {
  viewStore: ReleaseDetailsViewStore
  nnrOrder: INonNumericReleaseItem
  row: MRT_Row<IOrderItem>
}
export const NonNumericReleaseDetailsTableActions = observer((props: Props) => {
  const { t } = useTranslate()
  const { appViewStore } = usePlanningStore()
  const { viewStore, row, nnrOrder } = props
  const navigate = useNavigate()

  const onConfirmDelete = useCallback(
    async (row: MRT_Row<IOrderItem>) => {
      const order = row.original.data
      const journey = row.original.containerJourney

      let confirm = true

      if (
        order.carrierVisitType === 'Truck' &&
        journey?.status === ContainerJourneyStatus.Arrived
      ) {
        confirm = await appViewStore.setOpenConfirmDialog(
          true,
          t('theCarrierHasArrivedForPickup', 'The {carrier} has arrived for pickup.', {
            carrier: t(_.lowerFirst(order.carrierVisitType) ?? 'truck').toLocaleLowerCase(),
          }),
          t('unassignContainerConfirmationTitle', 'Unassign container {container}?', {
            container: order.containerNumber,
          }),
          undefined,
          {
            primaryActionText: t('yesUnassign', 'Yes, unassign'),
            secondaryActionText: t('noCancel', 'No, cancel'),
          },
        )
      }

      if (confirm) {
        try {
          appViewStore.showLoader()
          await nnrOrderService.unassignContainer({
            nonNumericOrderId: order.nonNumericOrderId!,
            orderId: order.id,
          })
          appViewStore.setShowAlert('success', t('containerUnassigned', 'Container unassigned'))
        } catch (error) {
          appViewStore.setShowAlert(
            'error',
            t('failedToUnassignContainer', 'Failed to unassign container'),
          )
        } finally {
          appViewStore.hideLoader()
        }
      }
    },
    [appViewStore, t],
  )

  const options = useCallback(
    (row: MRT_Row<IOrderItem>) => {
      const orderItem = row.original
      const id = orderItem.id

      const options: ButtonOption[] = [
        {
          label: t('view', 'View'),
          onClick: () => navigate(`${PATHS.visits.containerVisits.url}/order/${id}`),
        },
        {
          label: t('unassign', 'Unassign'),
          disabled: !orderItem.canUnassignFromRelease,
          onClick: () => onConfirmDelete(row),
        },
      ]
      return options
    },
    [navigate, onConfirmDelete, t],
  )

  if (row.original.data.containerNumber) {
    return <OptionsButton options={options(row)} position='right' />
  } else {
    return (
      <Button
        disabled={nnrOrder.isExpired}
        variant='contained'
        color='inherit'
        size='small'
        data-cy='assign-nnr-order'
        onClick={async () => {
          viewStore.setSelectedOrder(row.original.data)
          viewStore.setIsAssignDialogOpen(true)
        }}
      >
        {t('assign', 'Assign')}
      </Button>
    )
  }
})
